/**
 * Prix affichés sur le site (dashboard, etc.).
 * Mêmes montants en USD (site US) et EUR (site FR).
 * À aligner avec les produits/prix créés dans Stripe (STRIPE_PRICES_US / STRIPE_PRICES_FR).
 */

/** Site US marketing (/, /dashboard) — essai à l’unité et crédits extra. Doit correspondre aux produits Stripe (STRIPE_PRICES_US : trial, trial_drone, extra_credit, extra_credit_drone). */
export const PRICING_US_HOME = {
  trial: { withoutDrone: 150, withDrone: 190 },
  extraCredit: { withoutDrone: 150, withDrone: 190 },
} as const;

export type UsHomeVolumePlan = {
  videos: number;
  savingsLabel: string;
  unitPrice: number;
  monthlyPrice: number;
  popular?: boolean;
};

/** IDs de plan US home (checkout + abonnement) — alignés avec STRIPE_PRICES_US (starter5, …). */
export const US_HOME_PLAN_IDS = ['starter5', 'professional10', 'enterprise20'] as const;
export type UsHomePlanId = (typeof US_HOME_PLAN_IDS)[number];

const US_HOME_TIERS: { planId: UsHomePlanId; videos: number; unitDiscount: number; savingsLabel: string }[] = [
  { planId: 'starter5', videos: 5, unitDiscount: 0, savingsLabel: '' },
  { planId: 'professional10', videos: 10, unitDiscount: 10, savingsLabel: 'Save 7%' },
  { planId: 'enterprise20', videos: 20, unitDiscount: 20, savingsLabel: 'Save 13%' },
];

export function usHomeSubscriptionMonthlyUsd(planId: UsHomePlanId, withDrone: boolean): number {
  const base = withDrone ? PRICING_US_HOME.extraCredit.withDrone : PRICING_US_HOME.extraCredit.withoutDrone;
  const tier = US_HOME_TIERS.find((t) => t.planId === planId);
  if (!tier) return 0;
  return (base - tier.unitDiscount) * tier.videos;
}

export function buildUsHomeVolumePlans(baseUnit: number): UsHomeVolumePlan[] {
  return US_HOME_TIERS.map((t, idx) => {
    const unitPrice = baseUnit - t.unitDiscount;
    const monthlyPrice = unitPrice * t.videos;
    return {
      videos: t.videos,
      savingsLabel: t.savingsLabel,
      unitPrice,
      monthlyPrice,
      popular: idx === 1,
    };
  });
}

/* ─── FR Agents (/fr) — essai + abonnements en EUR ─── */

export const PRICING_FR_HOME = {
  trial: { withoutDrone: 150, withDrone: 190 },
  extraCredit: { withoutDrone: 150, withDrone: 190 },
} as const;

export type FrHomePlanId = 'starter' | 'professional' | 'enterprise';
export const FR_HOME_PLAN_IDS: readonly FrHomePlanId[] = ['starter', 'professional', 'enterprise'] as const;

const FR_HOME_TIERS: { planId: FrHomePlanId; videos: number; unitPrice: { standard: number; drone: number }; savingsLabel: string }[] = [
  { planId: 'starter', videos: 3, unitPrice: { standard: 150, drone: 190 }, savingsLabel: '' },
  { planId: 'professional', videos: 6, unitPrice: { standard: 140, drone: 180 }, savingsLabel: 'Éco. 7%' },
  { planId: 'enterprise', videos: 9, unitPrice: { standard: 130, drone: 170 }, savingsLabel: 'Éco. 13%' },
];

export type FrHomeVolumePlan = {
  videos: number;
  savingsLabel: string;
  unitPrice: number;
  monthlyPrice: number;
  popular?: boolean;
};

export function buildFrHomeVolumePlans(withDrone: boolean): FrHomeVolumePlan[] {
  return FR_HOME_TIERS.map((t, idx) => {
    const unit = withDrone ? t.unitPrice.drone : t.unitPrice.standard;
    return {
      videos: t.videos,
      savingsLabel: t.savingsLabel,
      unitPrice: unit,
      monthlyPrice: unit * t.videos,
      popular: idx === 1,
    };
  });
}

/* ─── FR Partenaires (/fr/partenaires) — mêmes montants que PRICING (EUR) ─── */

export const PRICING = {
  /** Try One Property / Essayer une propriété — partenaires (sans drone 95, avec drone 135) */
  trial: { withoutDrone: 95, withDrone: 135 },
  /** 3 vidéos/mois — partenaires : Standard 285/mo, + Drone 405/mo */
  starter: { withoutDrone: 285, withDrone: 405 },
  /** 6 vidéos/mois — partenaires : Standard 510/mo, + Drone 750/mo */
  professional: { withoutDrone: 510, withDrone: 750 },
  /** 9 vidéos/mois — partenaires : Standard 720/mo, + Drone 1080/mo */
  enterprise: { withoutDrone: 720, withDrone: 1080 },
  /** Crédit supplémentaire partenaires : sans drone 95, avec drone 135 (USD/EUR) */
  extraCredit: { withoutDrone: 95, withDrone: 135 },
} as const;
